// App shell — Sidebar + AppHeader + main content. Routes opt in by wrapping
// their tree. Routes that need a different layout (Login, NotFound) skip this.
//
// Die App-Kopfzeile (`AppHeader`) sitzt seit dem Redesign-Paket 2 fest in der
// rechten Spalte und ersetzt die frueher pro Route selbst gebauten Seitenkoepfe;
// Routen befuellen sie nur noch ueber `usePageHeader` (lib/pageHeader.ts).
// Der Gast-Zweig weiter unten behaelt bewusst seine eigene schlanke Kopfzeile —
// externe Gaeste sollen keine App-Navigation sehen.

import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import { useMe } from "../lib/auth";
import { api } from "../lib/api";
import { Sidebar } from "./Sidebar";
import { AppHeader } from "./AppHeader";
import { MobileNav } from "./MobileNav";
import { ToastHost } from "./ToastHost";
import { ConfirmDialogHost } from "./ConfirmDialogHost";

interface LayoutProps {
  children: ComponentChildren;
  activeProjectId?: number | null;
}

interface GuestScope {
  task_id: number | null;
  project_id: number | null;
}

/** Ist `location` eine der Routen, die dieser Gast-Scope erreichen darf?
 *  (/guest-settings ist standalone wie /guest-task/​/external — kein Layout,
 *  taucht hier also nie als `location` auf.) */
function guestRouteAllowed(scope: GuestScope, location: string): boolean {
  if (scope.task_id != null) return location === "/guest-task";
  return location === "/dashboard" || location.startsWith("/p/");
}

export function Layout(props: LayoutProps): preact.JSX.Element {
  const { children, activeProjectId } = props;
  const me = useMe();
  const [location, setLocation] = useLocation();
  const [guestScope, setGuestScope] = useState<GuestScope | null>(null);

  // Routes wrapped by Layout require auth.
  useEffect(() => {
    if (!me.loading && !me.user) {
      setLocation("/login");
    }
  }, [me.loading, me.user, setLocation]);

  // Externe Gäste dürfen NUR ihre eigene Landingpage erreichen — sonst würde
  // z.B. manuell eingetipptes /settings voll rendern (die meisten Sub-Fetches
  // 403en zwar serverseitig, aber die Seite selbst zeigt sich trotzdem, siehe
  // Layout ohne Guard). /api/external/me liefert den Scope (task- vs.
  // projekt-skopiert), den /api/me nicht kennt.
  useEffect(() => {
    if (!me.user?.is_external) {
      setGuestScope(null);
      return;
    }
    let cancelled = false;
    void api<GuestScope>("/api/external/me", { silent401: true })
      .then((scope) => {
        if (!cancelled) setGuestScope(scope);
      })
      .catch(() => {
        // Einladung abgelaufen/widerrufen zwischen Login und jetzt — überall
        // sonst sperrt der Server ohnehin hart, hier nur den Guard schließen.
        if (!cancelled) setGuestScope({ task_id: null, project_id: null });
      });
    return () => {
      cancelled = true;
    };
  }, [me.user?.is_external]);

  useEffect(() => {
    if (!guestScope) return;
    if (!guestRouteAllowed(guestScope, location)) {
      setLocation(guestScope.task_id != null ? "/guest-task" : "/dashboard");
    }
  }, [guestScope, location, setLocation]);

  if (me.loading) {
    return (
      <main class="min-h-dvh flex items-center justify-center text-sm text-[color:var(--color-on-surface-variant)]">
        Lade…
      </main>
    );
  }
  if (!me.user) return <></>;

  // Sprint 3 Story 4: externe Gäste bekommen ein eigenes minimalistisches
  // Layout ohne Sidebar — nur Banner + Children. Scope noch nicht geladen
  // oder Route für diesen Gast-Scope nicht erlaubt → Kinder nicht rendern
  // (der Redirect-Effect oben übernimmt), stattdessen ein leichter Lade-Zustand.
  if (me.user.is_external) {
    const showChildren = guestScope != null && guestRouteAllowed(guestScope, location);
    return (
      <div class="min-h-dvh flex flex-col">
        <header class="border-b border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-6 py-3 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <p class="text-lg font-bold text-[color:var(--color-on-surface)]">juuplan</p>
            <span class="inline-flex items-center rounded-full bg-[color:var(--color-tertiary-container)] px-2 py-0.5 text-label font-medium uppercase tracking-wider text-[color:var(--color-on-tertiary-container)]">
              Externer Gast
            </span>
          </div>
          <div class="flex items-center gap-2 text-xs">
            <span class="text-[color:var(--color-on-surface-variant)]">
              {me.user.name} · {me.user.email}
            </span>
            <a
              href="/guest-settings"
              class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
            >
              Einstellungen
            </a>
            <button
              type="button"
              onClick={() => {
                void fetch("/api/auth/logout", { method: "POST", credentials: "include" });
                try { localStorage.removeItem("juuplan:teams_bearer"); } catch {}
                window.location.href = "/external";
              }}
              class="rounded border border-[color:var(--color-outline-variant)] px-2 py-1 text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
            >
              Abmelden
            </button>
          </div>
        </header>
        <main class="flex-1 min-w-0">
          {showChildren ? (
            children
          ) : (
            <p class="p-6 text-sm text-[color:var(--color-on-surface-variant)]">Lade…</p>
          )}
        </main>
        <ToastHost />
        <ConfirmDialogHost />
      </div>
    );
  }

  return (
    <div class="flex min-h-dvh">
      <Sidebar activeProjectId={activeProjectId} />
      {/* Rechte Spalte: Kopfzeile UEBER dem Inhalt, aber neben der Seitenleiste
          — nicht ueber ihr. `min-w-0` bleibt (Projekt-Konvention gegen
          Querscrollen in Flex-Zeilen). */}
      <div class="flex-1 min-w-0 min-h-dvh flex flex-col">
        <AppHeader />
        {/* Bottom padding on mobile reserves room for the fixed MobileNav bar
            (56px + safe-area) so content never hides behind it. */}
        <main class="flex-1 min-w-0 pb-[calc(56px+env(safe-area-inset-bottom))] md:pb-0">
          {children}
        </main>
      </div>
      <MobileNav />
      <ToastHost />
      <ConfirmDialogHost />
    </div>
  );
}
